import { useAssetsStore } from '@/stores/assets'
import { memberPaymentMethodListNewApi } from '@/api/member'
import { PayMethod, paymentMethodNames, SELL_RECEIVE_PAY_METHOD_ORDER, UserRole } from '@/enums'
import { showConfirm } from './dialog'
import router from '@/router'

type CacheEntry = {
  userId: number | null
  data: QuickSellPaymentCheckData
}

/**
 * 单独验证手机号绑定
 *
 * @throws 如果验证失败或用户取消，抛出异常
 */
export async function verifyPhoneBind(): Promise<void> {
  const assetsStore = useAssetsStore()
  const userProfile = assetsStore.userProfile

  if (!userProfile) {
    await assetsStore.refreshProfile()
  }

  if (assetsStore.userProfile?.phone_bind !== true) {
    const result = await showConfirm('安全提示', '为了您的安全，请您前往绑定手机号', {
      confirmText: '前往认证',
    })
    if (result) {
      await router.push({ name: 'BindPhoneStep1', query: { bind: 'phone' } })
      throw new Error('需要绑定手机号')
    } else {
      throw new Error('需要绑定手机号')
    }
  }
}

/**
 * 单独验证实名认证
 *
 * @throws 如果验证失败或用户取消，抛出异常
 */
export async function verifyKYC(): Promise<void> {
  const assetsStore = useAssetsStore()
  const userProfile = assetsStore.userProfile

  if (!userProfile) {
    await assetsStore.refreshProfile()
  }

  if (assetsStore.unverified) {
    const result = await showConfirm(
      '安全提示',
      '您未进行身份验证，资金交易需要进行认证才可交易哦',
      {
        confirmText: '前往认证',
      },
    )
    if (result) {
      await router.push({ name: 'VerifyReal' })
      throw new Error('需要实名认证')
    } else {
      throw new Error('需要实名认证')
    }
  }
}

/**
 * 单独验证支付密码
 *
 * @throws 如果验证失败或用户取消，抛出异常
 */
export async function verifyPaymentPassword(): Promise<void> {
  const assetsStore = useAssetsStore()
  const userProfile = assetsStore.userProfile

  if (!userProfile) {
    await assetsStore.refreshProfile()
  }

  if (assetsStore.userProfile?.has_pin !== true) {
    const result = await showConfirm('温馨提示', '您还未设置支付密码哦', {
      confirmText: '前往设置',
    })
    if (result) {
      await router.push('/user/set-pin')
      throw new Error('需要设置支付密码')
    } else {
      throw new Error('需要设置支付密码')
    }
  }
}

/**
 * 单独验证收付款方式
 *
 * @throws 如果验证失败或用户取消，抛出异常
 */
export async function verifyPaymentMethod(): Promise<void> {
  const assetsStore = useAssetsStore()
  const userProfile = assetsStore.userProfile

  if (!userProfile) {
    await assetsStore.refreshProfile()
  }

  if (assetsStore.userProfile?.has_pay_method !== true) {
    const result = await showConfirm(
      '需要设置收付款方式',
      '请先添加至少一个收付款方式以便进行交易',
      {
        confirmText: '去设置',
      },
    )
    if (result) {
      await router.push('/user/pay-methods')
      throw new Error('需要设置收付款方式')
    } else {
      throw new Error('需要设置收付款方式')
    }
  }
}

/** 快速卖币支付方式校验数据（与接口一致） */
export type QuickSellPaymentCheckData = {
  required: string[]
  myMethods: string[]
}

let quickSellCheckCached: CacheEntry | null = null
let quickSellCheckInflight: Promise<QuickSellPaymentCheckData | null> | null = null

/**
 * 清除快速卖币支付方式校验缓存（收付款增删后应调用，避免点击仍用旧数据）
 */
export function clearQuickSellPaymentCheckCache(): void {
  quickSellCheckCached = null
}

/**
 * 快速卖币：会员已绑定的收款方式与后台配置的快速卖币方式无交集时，弹窗引导去绑定。
 * @returns true 可进入快速卖币页；false 已跳转收付款或用户取消
 */
// export async function verifyQuickSellPaymentMethods(): Promise<boolean> {
//   try {
//     const snapshot = await loadQuickSellPaymentCheck()
//     if (!snapshot) return true
//
//     const { required, myMethods } = snapshot
//     if (required.length === 0) return true
//
//     const hasMatch = required.some((code) => myMethods.includes(code))
//     if (hasMatch) return true
//
//     const missing = required.filter((code) => !myMethods.includes(code))
//     const orderedMissing = SELL_RECEIVE_PAY_METHOD_ORDER.filter((m) => missing.includes(m))
//     const firstMissing = (orderedMissing[0] ?? missing[0]) as PayMethod
//     const label = paymentMethodNames[firstMissing] ?? firstMissing
//
//     try {
//       await showConfirm('支付方式绑定', `无对应支付方式\n请您绑定${label}`, {
//         confirmText: '前往绑定',
//         cancelText: '取消',
//       })
//       await router.push({ name: 'PayMethods', query: { tab: firstMissing } })
//     } catch {
//       // 用户取消或关闭弹窗
//     }
//     return false
//   } catch {
//     // 接口异常不阻断入口
//     return true
//   }
// }

export async function verifyQuickSellPaymentMethods(): Promise<boolean> {
  try {
    const res = await memberPaymentMethodListNewApi({ purpose: 2 })
    const items = res.data ?? []
    if (items.length === 0) return true

    const hasMatch = items.some((item) => item.pay_methods.length > 0)
    if (hasMatch) return true

    const missingCodes = items
      .filter((item) => item.pay_methods.length === 0)
      .map((item) => item.code)
    const orderedMissing = SELL_RECEIVE_PAY_METHOD_ORDER.filter((m) => missingCodes.includes(m))
    const firstCode = orderedMissing[0] ?? missingCodes[0]
    const firstItem = items.find((item) => item.code === firstCode)
    const label = firstItem?.name ?? paymentMethodNames[firstCode as PayMethod] ?? firstCode

    try {
      await showConfirm('支付方式绑定', `无对应支付方式\n请您绑定${label}`, {
        confirmText: '前往绑定',
        cancelText: '取消',
      })
      await router.push({ name: 'PayMethods', query: { tab: firstCode } })
    } catch {
      // 用户取消或关闭弹窗
    }
    return false
  } catch {
    // 接口异常不阻断入口
    return true
  }
}

/**
 * 用户交易前验证
 * 逐步验证：先手机号 → 再实名(KYC) → 支付密码 → 收付款方式。
 * 某一项未通过时弹窗提示，用户选择「去绑定/去认证」则跳转对应页面并中止，不再执行后续验证。
 *
 * @throws 任何一项验证未通过或用户取消时抛出异常
 *
 * @example
 * ```ts
 * try {
 *   await verifyUserBeforeTrade()
 *   // 所有验证通过，继续交易流程
 * } catch (error) {
 *   // 验证失败，已跳转或已阻断
 * }
 * ```
 */
export async function verifyUserBeforeTrade(): Promise<void> {
  await verifyPhoneBind() // 未绑定手机 → 跳转手机绑定页并中止
  await verifyKYC() // 未实名 → 跳转 KYC 页并中止
  await verifyPaymentPassword()
  await verifyPaymentMethod()
}

export async function verifyUserBeforeBuyCoin(): Promise<void> {
  await verifyPhoneBind() // 未绑定手机 → 跳转手机绑定页并中止
  await verifyKYC() // 未实名 → 跳转 KYC 页并中止
  await verifyPaymentPassword()
  // 不需要验证这个，因为买币的时候会验证对应收款方式
  // await verifyPaymentMethod()
}

export function ifIsAgent(role: UserRole): boolean {
  switch (role) {
    case UserRole.Agent:
    case UserRole.SystemAgent:
    case UserRole.MerchantAgent:
      return true
    default:
      return false
  }
}
